/**
 * ASR service — Additional Services Requests (mid-project change orders). Separate from the fee
 * engine: a simple line-item fee (Fixed/Hourly) on the Proposal Line Items table, summed to Total Fee.
 *
 *   ASR Proposals     tblIVbzoUa7HH2Olz  (per-project ASR Number, Posted + Approval Confirmation -> Status)
 *   Proposal Line Items tblxZcDHTIKI0SdSc (Item / Fee Type / Hours / Rate -> Fee)
 *
 * Issued to the client via the Client Hub (asr_review module) where they review and e-sign; the
 * sign write-back flips Approval Confirmation Message -> Executed.
 */
import { apiGet, apiGetOne, apiPatch, apiPost, apiDelete } from './_api.js'

const TBL_ASR = 'tblIVbzoUa7HH2Olz'
const TBL_LI = 'tblxZcDHTIKI0SdSc'

export const ASR_FEE_TYPES = ['Fixed', 'Hourly', 'Fixed+Hourly']
export const LINE_FEE_TYPES = ['Fixed', 'Hourly']

function num(v) { const n = Number(v); return isNaN(n) ? 0 : n }
function first(v) { return Array.isArray(v) ? v[0] : v }
function attUrl(v) { return Array.isArray(v) && v[0] ? v[0].url : null }

function normAsr(r) {
  const f = (r && r.fields) || {}
  return {
    id: r.id,
    number: f['ASR Number'] || null,
    name: f['Name'] || '',
    title: f['ASR Title'] || '',
    feeType: f['Fee Type'] || 'Fixed',
    status: f['Status'] || 'Drafting',
    opportunityId: first(f['Opportunity']) || null,
    totalFee: num(f['Total Fee']),
    stipulations: f['Stipulations'] || '',
    issueDate: f['Issue Date'] || null,
    posted: !!f['Posted'],
    approved: !!f['Approval Confirmation Message'],
    postedDoc: attUrl(f['Posted Document']),
    signedDoc: attUrl(f['Signed Document']),
    lineItemIds: Array.isArray(f['Proposal Line Items']) ? f['Proposal Line Items'] : [],
    raw: f,
  }
}

function normLine(r) {
  const f = (r && r.fields) || {}
  return { id: r.id, item: f['Item'] || '', feeType: f['Fee Type'] || 'Fixed', hours: num(f['Hours']), rate: num(f['Rate']), fee: num(f['Fee']) }
}

/* ── Reads ───────────────────────────────────────────────────────────────── */
export async function getAsrsForProject(projectId) {
  const formula = `FIND("${projectId}",ARRAYJOIN({Project Record UID (from Project/Opportunity)},","))`
  const recs = await apiGet(TBL_ASR, 'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=ASR+Number&sort%5B0%5D%5Bdirection%5D=desc')
  return recs.map(normAsr)
}
export async function getAsr(id) { const r = await apiGetOne(TBL_ASR, id); return r ? normAsr(r) : null }

export async function getAsrLineItems(lineItemIds) {
  const ids = (lineItemIds || []).filter(Boolean)
  if (!ids.length) return []
  const out = []
  for (let i = 0; i < ids.length; i += 50) {
    const formula = 'OR(' + ids.slice(i, i + 50).map(x => `RECORD_ID()='${x}'`).join(',') + ')'
    const recs = await apiGet(TBL_LI, 'filterByFormula=' + encodeURIComponent(formula))
    out.push(...recs.map(normLine))
  }
  return out
}

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim)
   instead of throwing — surface it so a bad write shows up as a real error in the editor
   instead of silently doing nothing. */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

/* ── Writes ──────────────────────────────────────────────────────────────── */
export async function createAsr({ opportunityId, title, feeType, issueDate, stipulations }) {
  // next ASR Number for the project
  let next = 1
  try { const existing = await getAsrsForProject(opportunityId); next = existing.reduce((m, a) => Math.max(m, a.number || 0), 0) + 1 } catch (_) {}
  const fields = {
    'ASR Title': title || `ASR No. ${next}`,
    'Opportunity': [opportunityId],
    'ASR Number': next,
    'Fee Type': feeType || 'Fixed',
    'Issue Date': issueDate || new Date().toISOString().slice(0, 10),
  }
  if (stipulations) fields['Stipulations'] = stipulations
  const res = throwOnError(await apiPost(TBL_ASR, fields, { typecast: true }), 'ASR')
  return res && res.id ? normAsr(res) : null
}

export async function updateAsr(id, { title, feeType, issueDate, stipulations } = {}) {
  const f = {}
  if (title !== undefined) f['ASR Title'] = title
  if (feeType !== undefined) f['Fee Type'] = feeType
  if (issueDate !== undefined) f['Issue Date'] = issueDate
  if (stipulations !== undefined) f['Stipulations'] = stipulations
  const res = throwOnError(await apiPatch(TBL_ASR, id, f, { typecast: true }), 'ASR')
  return res && res.id ? normAsr(res) : null
}

function lineFields(asrId, row) {
  return { Item: row.item || '', Proposal: [asrId], 'Fee Type': row.feeType || 'Fixed', Hours: num(row.hours), Rate: num(row.rate) }
}
/** Sync the ASR's line items: create new, update changed, delete removed. rows: [{id?, item, feeType, hours, rate}]. */
export async function saveAsrLineItems(asrId, rows, priorIds = []) {
  const keep = new Set()
  for (const row of rows) {
    if (!row.item && !row.hours && !row.rate) continue
    if (row.id) { keep.add(row.id); await apiPatch(TBL_LI, row.id, lineFields(asrId, row), { typecast: true }) }
    else { const res = await apiPost(TBL_LI, lineFields(asrId, row), { typecast: true }); if (res && res.id) keep.add(res.id) }
  }
  for (const id of priorIds) if (!keep.has(id)) { try { await apiDelete(TBL_LI, id) } catch (_) {} }
}

export function deleteAsr(id) { return apiDelete(TBL_ASR, id) }

/* ── PDF (api/asr-pdf.js) ────────────────────────────────────────────────── */
async function clerkToken() { try { return typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null } catch { return null } }
export async function previewAsrPdf(asrId) {
  const token = await clerkToken()
  const res = await fetch('/api/asr-pdf', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) }, body: JSON.stringify({ asrId, working: true }) })
  if (!res.ok) { let e; try { e = (await res.json()).error } catch (_) {} throw new Error(e || 'PDF failed') }
  return URL.createObjectURL(await res.blob())
}
export async function freezeAsrPdf(asrId) {
  const token = await clerkToken()
  const res = await fetch('/api/asr-pdf', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) }, body: JSON.stringify({ asrId, freeze: true }) })
  const d = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(d.error || 'Freeze failed')
  return d
}
export function markAsrPosted(asrId, { memberId } = {}) {
  const f = { 'Posted': true }
  if (memberId) f['Post by Member'] = [memberId]
  return apiPatch(TBL_ASR, asrId, f, { typecast: true })
}

/** TEST ONLY (debug-gated UI): send a signed ASR back to the journey unsigned so the flow re-tests. */
export async function resetAsrSignature(asrId) {
  const token = await clerkToken()
  const res = await fetch('/api/asr-reset', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) }, body: JSON.stringify({ asrId }) })
  const d = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(d.error || 'Reset failed')
  return d
}
